import { pageTitle } from '@palaccount/config';
import type { ReorderSuggestionRow } from '@palaccount/contracts';
import { Search } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';

import { Amount, Ltr, Qty } from '@/components/amount';
import { Pager } from '@/components/pager';
import { Gated } from '@/components/permission-gate';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Typeahead } from '@/components/ui/typeahead';
import { useItemCategories, useManufacturers } from '@/features/inventory/api';
import { useWarehouses } from '@/features/inventory/stock-api';
import { fromMilliQty, toMilliQty } from '@/features/inventory/stock-format';
import { usePartnerPicker } from '@/features/partners/partner-picker';
import { formatQty, trimTrailingZeros } from '@/lib/format';
import { localizedName } from '@/lib/localized-name';
import { amountOf } from '@/lib/money';
import { usePagedState, usePageRange } from '@/lib/paging';
import { localizeServerCause } from '@/lib/server-messages';
import { useBaseCurrency } from '@/lib/session';
import { cn } from '@/lib/utils';

import { PurchasesTabs } from './purchases-tabs';
import {
  DEFAULT_REORDER_FILTERS,
  useBuildReorder,
  useReorderSuggestions,
  type ReorderFilters,
} from './reorder-api';

/** The filters outlive the visit — a buyer works one warehouse for weeks. */
const STORAGE_KEY = 'reorder.filters';

function readStoredFilters(): ReorderFilters {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null) return DEFAULT_REORDER_FILTERS;
    const parsed = JSON.parse(raw) as Partial<ReorderFilters> | null;
    if (parsed === null || typeof parsed !== 'object') return DEFAULT_REORDER_FILTERS;
    return {
      warehouseId: typeof parsed.warehouseId === 'string' ? parsed.warehouseId : '',
      categoryId: typeof parsed.categoryId === 'string' ? parsed.categoryId : '',
      manufacturerId: typeof parsed.manufacturerId === 'string' ? parsed.manufacturerId : '',
      supplierId: typeof parsed.supplierId === 'string' ? parsed.supplierId : null,
      belowMinimumOnly:
        typeof parsed.belowMinimumOnly === 'boolean' ? parsed.belowMinimumOnly : true,
      includeZero: typeof parsed.includeZero === 'boolean' ? parsed.includeZero : false,
      q: typeof parsed.q === 'string' ? parsed.q : '',
    };
  } catch {
    return DEFAULT_REORDER_FILTERS;
  }
}

function storeFilters(filters: ReorderFilters): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
  } catch {
    /* storage refused — the filters still apply for this visit */
  }
}

/** The wire's own bound (QtyStringSchema): positive, ≤ 3 decimals, and no more than the unit allows. */
function qtyValid(value: string, precision: number): boolean {
  const text = value.trim();
  if (!/^\d{1,12}(\.\d{1,3})?$/.test(text)) return false;
  if (/^0+(\.0+)?$/.test(text)) return false;
  const decimals = text.split('.')[1]?.length ?? 0;
  return decimals <= precision;
}

/** The `low` ladder, as the server defines it: a set minimum, reached. */
function belowMinimum(row: ReorderSuggestionRow): boolean {
  const point = toMilliQty(row.reorderPoint);
  const available = toMilliQty(row.available);
  return point !== null && available !== null && point > 0n && available <= point;
}

/**
 * Wave 5A — the reorder builder. Suggestions come from the API (the formula
 * lives at the contract); this screen lets a buyer narrow them, tick lines,
 * correct a quantity, and turn the ticks into a PUR-01 draft in one click.
 * It is a TOOL, not a register: no document list, no status chips — the
 * draft it creates is the record, and the editor opens on it.
 */
export function ReorderPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const baseCurrency = useBaseCurrency();

  // Two copies of the filters: what the bar shows, and what the grid was
  // asked for. Apply moves one onto the other, so half-typed filters never
  // fire a query (and the pager resets with the applied set).
  const [draft, setDraft] = useState<ReorderFilters>(readStoredFilters);
  const [applied, setApplied] = useState<ReorderFilters>(draft);
  const paging = usePagedState();
  const { data, isPending, isError } = useReorderSuggestions(applied, paging.page, paging.pageSize);
  const range = usePageRange(data, paging.pageSize);
  const rows = useMemo(() => data?.rows ?? [], [data]);

  const { data: warehouses } = useWarehouses({ status: 'active' });
  const { data: categories } = useItemCategories(true);
  const { data: manufacturers } = useManufacturers(true);
  const supplierFilter = usePartnerPicker(draft.supplierId, { type: 'supplier' });

  /** The order's supplier — starts as the filter's, and is its own choice after. */
  const [orderSupplierId, setOrderSupplierId] = useState<string | null>(draft.supplierId);
  const orderSupplier = usePartnerPicker(orderSupplierId, { type: 'supplier' });

  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [qtyEdits, setQtyEdits] = useState<Record<string, string>>({});
  const build = useBuildReorder();

  useEffect(() => {
    document.title = pageTitle(t('reorder.title'));
  }, [t]);

  const apply = () => {
    storeFilters(draft);
    paging.change(setApplied)(draft);
    if (draft.supplierId && orderSupplierId === null) setOrderSupplierId(draft.supplierId);
  };

  const qtyOf = (row: ReorderSuggestionRow): string =>
    qtyEdits[row.itemId] ?? trimTrailingZeros(row.suggestedQty);

  const toggle = (itemId: string, on: boolean) =>
    setSelected((current) => {
      const next = new Set(current);
      if (on) next.add(itemId);
      else next.delete(itemId);
      return next;
    });

  const selectedRows = rows.filter((row) => selected.has(row.itemId));
  const invalidSelected = selectedRows.some((row) => !qtyValid(qtyOf(row), row.unitPrecision));

  // N-14: a sum across items has no single unit, so the footer prints it bare.
  const totalQty = selectedRows.reduce((sum, row) => {
    const milli = qtyValid(qtyOf(row), row.unitPrecision) ? toMilliQty(qtyOf(row)) : null;
    return milli === null ? sum : sum + milli;
  }, 0n);
  const lineValue = (row: ReorderSuggestionRow): string | null =>
    qtyValid(qtyOf(row), row.unitPrecision)
      ? amountOf(row.lastCost, row.currency).times(qtyOf(row).trim()).rounded().toFixed2()
      : null;
  const totalValue = selectedRows
    .reduce((sum, row) => {
      const value = lineValue(row);
      return value === null ? sum : sum.plus(amountOf(value, row.currency));
    }, amountOf('0', baseCurrency))
    .toFixed2();

  const canBuild =
    orderSupplierId !== null && selectedRows.length > 0 && !invalidSelected && !build.isPending;

  const create = () => {
    if (!canBuild || orderSupplierId === null) return;
    build.mutate(
      {
        supplierId: orderSupplierId,
        lines: selectedRows.map((row) => ({ itemId: row.itemId, qty: qtyOf(row).trim() })),
      },
      {
        onSuccess: (created) => {
          void navigate(`/purchases/orders/${created.purchaseOrderId}`);
        },
      },
    );
  };

  const allOnPageSelected = rows.length > 0 && rows.every((row) => selected.has(row.itemId));

  return (
    <div className="grid gap-4">
      <PurchasesTabs />
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="me-auto text-lg font-bold">{t('reorder.title')}</h2>
      </div>
      <p className="text-sm text-muted-foreground">{t('reorder.intro')}</p>

      <Card>
        <CardContent className="grid gap-4">
          <form
            className="flex flex-wrap items-end gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              apply();
            }}
          >
            <div className="relative w-full max-w-[320px]">
              <Search
                className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
                aria-hidden
              />
              <Input
                aria-label={t('reorder.searchLabel')}
                placeholder={t('reorder.searchPh')}
                value={draft.q}
                onChange={(event) => setDraft({ ...draft, q: event.target.value })}
                className="h-10 ps-9 text-sm"
              />
            </div>
            <NativeSelect
              aria-label={t('reorder.filterWarehouse')}
              value={draft.warehouseId}
              onChange={(event) => setDraft({ ...draft, warehouseId: event.target.value })}
              className="h-10 w-auto text-sm"
            >
              <option value="">{t('reorder.filterWarehouseAll')}</option>
              {(warehouses ?? []).map((warehouse) => (
                <option key={warehouse.id} value={warehouse.id}>
                  {warehouse.name}
                </option>
              ))}
            </NativeSelect>
            <NativeSelect
              aria-label={t('reorder.filterCategory')}
              value={draft.categoryId}
              onChange={(event) => setDraft({ ...draft, categoryId: event.target.value })}
              className="h-10 w-auto text-sm"
            >
              <option value="">{t('reorder.filterCategoryAll')}</option>
              {(categories ?? []).map((category) => (
                <option key={category.id} value={category.id}>
                  {localizedName(category, i18n.language)}
                </option>
              ))}
            </NativeSelect>
            <NativeSelect
              aria-label={t('reorder.filterManufacturer')}
              value={draft.manufacturerId}
              onChange={(event) => setDraft({ ...draft, manufacturerId: event.target.value })}
              className="h-10 w-auto text-sm"
            >
              <option value="">{t('reorder.filterManufacturerAll')}</option>
              {(manufacturers ?? []).map((maker) => (
                <option key={maker.id} value={maker.id}>
                  {localizedName(maker, i18n.language)}
                </option>
              ))}
            </NativeSelect>
            <div className="w-full max-w-[280px]">
              {/* GEN-50: suppliers only, searched — the same picker as every document's supplier box. */}
              <Typeahead
                label={t('reorder.filterSupplier')}
                value={draft.supplierId}
                onChange={(id) => setDraft({ ...draft, supplierId: id })}
                options={supplierFilter.options}
                selected={supplierFilter.optionFor(draft.supplierId)}
                query={supplierFilter.query}
                onQueryChange={supplierFilter.setQuery}
                emptyLabel={t('picker.noMatchingSupplier')}
                loading={supplierFilter.loading}
              />
            </div>
            <Label htmlFor="reorder-below-min" className="flex items-center gap-2 font-normal">
              <Checkbox
                id="reorder-below-min"
                checked={draft.belowMinimumOnly}
                onCheckedChange={(checked) =>
                  setDraft({ ...draft, belowMinimumOnly: checked === true })
                }
              />
              {t('reorder.belowMinimumOnly')}
            </Label>
            <Label htmlFor="reorder-include-zero" className="flex items-center gap-2 font-normal">
              <Checkbox
                id="reorder-include-zero"
                checked={draft.includeZero}
                onCheckedChange={(checked) => setDraft({ ...draft, includeZero: checked === true })}
              />
              {t('reorder.includeZero')}
            </Label>
            <Button type="submit" variant="outline">
              {t('reorder.apply')}
            </Button>
          </form>

          {isPending ? (
            <div className="h-40 animate-pulse rounded-md bg-muted" aria-busy />
          ) : isError ? (
            <p role="alert" className="py-8 text-center text-sm text-destructive">
              {t('common.loadFailed')}
            </p>
          ) : rows.length === 0 ? (
            <div className="grid justify-items-center gap-2 py-12 text-center text-sm text-muted-foreground">
              <p>{t('reorder.empty')}</p>
              <Link to="/inventory" className="font-medium text-primary underline-offset-4 hover:underline">
                {t('reorder.emptyLink')}
              </Link>
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setSelected((current) => {
                      const next = new Set(current);
                      for (const row of rows) if (belowMinimum(row)) next.add(row.itemId);
                      return next;
                    })
                  }
                >
                  {t('reorder.selectBelowMinimum')}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={selected.size === 0}
                  onClick={() => setSelected(new Set())}
                >
                  {t('reorder.clearSelection')}
                </Button>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">
                      <Checkbox
                        aria-label={t('reorder.selectAll')}
                        checked={allOnPageSelected}
                        onCheckedChange={(checked) =>
                          setSelected((current) => {
                            const next = new Set(current);
                            for (const row of rows) {
                              if (checked === true) next.add(row.itemId);
                              else next.delete(row.itemId);
                            }
                            return next;
                          })
                        }
                      />
                    </TableHead>
                    <TableHead>{t('reorder.colCode')}</TableHead>
                    <TableHead>{t('reorder.colItem')}</TableHead>
                    <TableHead>{t('reorder.colUnit')}</TableHead>
                    <TableHead>{t('reorder.colBarcode')}</TableHead>
                    <TableHead className="text-end">{t('reorder.colOnHand')}</TableHead>
                    <TableHead className="text-end">{t('reorder.colAvailable')}</TableHead>
                    <TableHead className="text-end">{t('reorder.colMin')}</TableHead>
                    <TableHead className="text-end">{t('reorder.colMax')}</TableHead>
                    <TableHead className="text-end">{t('reorder.colSuggested')}</TableHead>
                    <TableHead className="text-end">{t('reorder.colLastCost')}</TableHead>
                    <TableHead className="text-end">{t('reorder.colLineValue')}</TableHead>
                    <TableHead>{t('reorder.colSupplier')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => {
                    const on = selected.has(row.itemId);
                    const valid = qtyValid(qtyOf(row), row.unitPrecision);
                    const value = lineValue(row);
                    return (
                      <TableRow
                        key={row.itemId}
                        data-state={on ? 'selected' : undefined}
                        className={cn(
                          'odd:bg-muted/30',
                          belowMinimum(row) && 'bg-amber-50/60 dark:bg-amber-950/20',
                        )}
                      >
                        <TableCell>
                          <Checkbox
                            aria-label={t('reorder.selectRow', { code: row.itemCode })}
                            checked={on}
                            onCheckedChange={(checked) => toggle(row.itemId, checked === true)}
                          />
                        </TableCell>
                        <TableCell>
                          <Ltr value={row.itemCode} className="font-mono text-sm" />
                        </TableCell>
                        <TableCell className="font-medium">{localizedName(row, i18n.language)}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{row.unit}</TableCell>
                        <TableCell>
                          {row.barcode ? <Ltr value={row.barcode} className="font-mono text-sm" /> : '—'}
                        </TableCell>
                        <TableCell className="text-end">
                          <Qty value={row.onHand} precision={row.unitPrecision} unit={row.unit} />
                        </TableCell>
                        <TableCell className="text-end">
                          <Qty
                            value={row.available}
                            precision={row.unitPrecision}
                            unit={row.unit}
                            className={cn(belowMinimum(row) && 'font-semibold text-amber-700 dark:text-amber-400')}
                          />
                        </TableCell>
                        <TableCell className="text-end">
                          <Ltr value={formatQty(row.reorderPoint, row.unitPrecision)} className="font-mono tabular-nums" />
                        </TableCell>
                        <TableCell className="text-end">
                          {row.maxLevel === null ? (
                            '—'
                          ) : (
                            <Ltr value={formatQty(row.maxLevel, row.unitPrecision)} className="font-mono tabular-nums" />
                          )}
                        </TableCell>
                        <TableCell className="text-end">
                          {/* N-4's editable exception: trimmed for typing; what the
                              row DRAWS beside it (on hand, available) obeys the unit. */}
                          <Input
                            dir="ltr"
                            inputMode="decimal"
                            aria-label={t('reorder.qtyFor', { code: row.itemCode })}
                            aria-invalid={on && !valid ? true : undefined}
                            value={qtyOf(row)}
                            onChange={(event) =>
                              setQtyEdits((current) => ({ ...current, [row.itemId]: event.target.value }))
                            }
                            className="h-9 w-28 text-end font-mono tabular-nums"
                          />
                        </TableCell>
                        <TableCell className="text-end">
                          <Amount value={row.lastCost} currency={row.currency} />
                        </TableCell>
                        <TableCell className="text-end">
                          {value === null ? '—' : <Amount value={value} currency={row.currency} />}
                        </TableCell>
                        <TableCell className="text-sm">
                          {row.preferredSupplierName ?? row.lastSupplierName ?? '—'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TableCell colSpan={9}>{t('reorder.footerSelected', { count: selectedRows.length })}</TableCell>
                    <TableCell className="text-end">
                      <span className="me-2 text-xs text-muted-foreground">{t('reorder.footerTotalQty')}</span>
                      <Ltr value={fromMilliQty(totalQty)} className="font-mono tabular-nums" />
                    </TableCell>
                    <TableCell colSpan={2} className="text-end">
                      <span className="me-2 text-xs text-muted-foreground">{t('reorder.footerTotalValue')}</span>
                      <Amount value={totalValue} currency={baseCurrency} />
                    </TableCell>
                    <TableCell />
                  </TableRow>
                </TableFooter>
              </Table>
              {(data?.total ?? 0) > 0 ? (
                <Pager
                  page={paging.page}
                  pages={range.pages}
                  first={range.first}
                  last={range.last}
                  total={range.total}
                  onPage={paging.setPage}
                  label={t('reorder.title')}
                />
              ) : null}
            </>
          )}

          <div className="flex flex-wrap items-end gap-3 border-t pt-4">
            <div className="w-full max-w-[320px]">
              <Typeahead
                label={t('reorder.orderFrom')}
                value={orderSupplierId}
                onChange={setOrderSupplierId}
                options={orderSupplier.options}
                selected={orderSupplier.optionFor(orderSupplierId)}
                query={orderSupplier.query}
                onQueryChange={orderSupplier.setQuery}
                emptyLabel={t('picker.noMatchingSupplier')}
                loading={orderSupplier.loading}
              />
            </div>
            {orderSupplierId === null && selectedRows.length > 0 ? (
              <p className="text-sm text-muted-foreground">{t('reorder.supplierRequired')}</p>
            ) : null}
            <div className="ms-auto">
              <Gated permission="purchasing.create" reasonKey="perm.cannot.createPurchaseOrders">
                <Button type="button" onClick={create} disabled={!canBuild}>
                  {t('reorder.build')}
                </Button>
              </Gated>
            </div>
          </div>
          {build.isError ? (
            <p role="alert" className="text-sm text-destructive">
              {localizeServerCause(t, build.error, 'reorder.buildFailed')}
            </p>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
